import { Directive } from '@angular/core';

export type StepHeaderMode = 'sequential' | 'tabs';

export type StepState = 'locked' | 'available' | 'complete';

export interface StepDefinition {
  id: string;
  label: string;
  icon?: string;       // optional for tab-mode (mat-icon name)
}

@Directive()
// Base class for step-based dialogs (no template here; subclasses render UI)
export abstract class StepDialogComponent {
  headerMode: StepHeaderMode = 'sequential';

  /** Steps must be set by the subclass (usually in ctor or ngOnInit). */
  steps: StepDefinition[] = [];

  /** 0-based current step index */
  stepIndex = 0;

  /** Tracks completed steps (by index) */
  private completed = new Set<number>();

  /** If sequential mode, this determines the furthest step the user can reach */
  private maxUnlockedIndex = 0;

  protected initSteps(steps: StepDefinition[], mode: StepHeaderMode = 'sequential', initialIndex = 0): void {
    this.steps = steps;
    this.headerMode = mode;
    this.stepIndex = Math.max(0, Math.min(initialIndex, steps.length - 1));
    this.completed.clear();
    this.maxUnlockedIndex = 0;
  }

  /** Call when a step is "done" (e.g., valid form, finished selection, etc.) */
  protected markStepComplete(index: number = this.stepIndex): void {
    this.completed.add(index);

    // in sequential mode, unlock the next step if we just completed the current unlocked step
    if (this.headerMode === 'sequential') {
      // unlock forward as far as contiguous completions allow
      while (this.completed.has(this.maxUnlockedIndex) && this.maxUnlockedIndex < this.steps.length - 1) {
        this.maxUnlockedIndex++;
      }
    }
  }

  isStepComplete(index: number): boolean {
    return this.completed.has(index);
  }

  /** Locked/available/complete state for UI */
  getStepState(index: number): StepState {
    if (this.isStepComplete(index)) {
      return 'complete';
    }

    if (this.headerMode === 'tabs') {
      return 'available';
    }

    // sequential
    return index <= this.maxUnlockedIndex ? 'available' : 'locked';
  }

  canNavigateToStep(index: number): boolean {
    return this.getStepState(index) !== 'locked';
  }

  goToStep(index: number): void {
    if (index < 0 || index >= this.steps.length) {
      return;
    }
    if (!this.canNavigateToStep(index)) {
      return;
    }
    this.stepIndex = index;
  }

  back(): void {
    this.goToStep(this.stepIndex - 1);
  }

  next(): void {
    this.goToStep(this.stepIndex + 1);
  }

  getStepBadgeCount(step: StepDefinition, index: number): number | null {
    return null;
  }

  /** Useful for the “tab” design: keep step number in the footer */
  get stepCounterText(): string {
    return `Step ${this.stepIndex + 1} of ${this.steps.length}`;
  }
}
